import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { jobsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './JobDetail.css';

const JOB_TYPE_LABELS = { full_time: 'دوام كامل', part_time: 'دوام جزئي', contract: 'عقد', freelance: 'عمل حر', internship: 'تدريب' };

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [application, setApplication] = useState({ cover_letter: '', resume_url: '' });

  useEffect(() => {
    jobsAPI.getJob(id).then(r => setJob(r.data.job)).catch(() => navigate('/jobs')).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return navigate('/login');
    setApplying(true);
    try {
      await jobsAPI.applyJob(id, application);
      toast.success('تم تقديم طلبك بنجاح! 🎉');
      setShowModal(false);
      setJob(prev => ({ ...prev, is_applied: true, applications_count: prev.applications_count + 1 }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ!');
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!job) return null;

  return (
    <div className="page-wrapper">
      <div className="container">
        <div className="job-detail-layout">
          {/* Main content */}
          <div className="job-detail-main">
            <div className="job-detail-card card">
              <div className="job-detail-header">
                <div className="org-info">
                  <div className="org-logo-lg">
                    {job.org_logo ? <img src={job.org_logo} alt={job.org_name} /> : <span>{(job.org_name || job.poster_name)?.[0]}</span>}
                  </div>
                  <div>
                    <h1 className="job-detail-title">{job.title}</h1>
                    <div className="job-detail-org">{job.org_name || job.poster_name}</div>
                  </div>
                </div>
                <div className="job-detail-badges">
                  <span className="badge badge-primary">{JOB_TYPE_LABELS[job.job_type]}</span>
                  {job.is_remote && <span className="badge badge-success">🌐 عن بُعد</span>}
                  {job.is_featured && <span className="badge badge-warning">⭐ مميز</span>}
                </div>
              </div>

              <div className="job-detail-meta">
                {job.location_city && <div className="meta-item">📍 {job.location_city}{job.location_country && `, ${job.location_country}`}</div>}
                <div className="meta-item">🏃 {job.sport_category}</div>
                {job.experience_required > 0 && <div className="meta-item">⏳ {job.experience_required}+ سنوات خبرة</div>}
                {job.deadline && <div className="meta-item">📅 آخر موعد: {new Date(job.deadline).toLocaleDateString('ar')}</div>}
                <div className="meta-item">👥 {job.applications_count} متقدم</div>
                <div className="meta-item">👁 {job.views} مشاهدة</div>
              </div>

              {(job.salary_min || job.salary_max) && (
                <div className="salary-box">
                  <span className="salary-label">الراتب المتوقع</span>
                  <span className="salary-value">
                    {job.salary_min && job.salary_max ? `${job.salary_min} - ${job.salary_max}` : job.salary_max || job.salary_min} {job.salary_currency}
                    <span className="salary-per">/ {job.salary_period === 'monthly' ? 'شهرياً' : 'سنوياً'}</span>
                  </span>
                </div>
              )}

              <div className="divider" />
              
              {job.description && (
                <div className="job-section">
                  <h3>وصف الوظيفة</h3>
                  <p className="job-text">{job.description}</p>
                </div>
              )}
              {job.requirements && (
                <div className="job-section">
                  <h3>المتطلبات</h3>
                  <p className="job-text">{job.requirements}</p>
                </div>
              )}
              {job.responsibilities && (
                <div className="job-section">
                  <h3>المهام والمسؤوليات</h3>
                  <p className="job-text">{job.responsibilities}</p>
                </div>
              )}
              {job.benefits && (
                <div className="job-section">
                  <h3>المزايا والمكافآت</h3>
                  <p className="job-text">{job.benefits}</p>
                </div>
              )}

              {(() => {
                let skills = job.skills_required;
                if (typeof skills === 'string') { try { skills = JSON.parse(skills); } catch { skills = []; } }
                return skills?.length > 0 ? (
                  <div className="job-section">
                    <h3>المهارات المطلوبة</h3>
                    <div className="skills-tags">{skills.map((s, i) => <span key={i} className="tag">{s}</span>)}</div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="job-detail-sidebar">
            <div className="apply-card card">
              <h3>تقدّم الآن</h3>
              {job.is_applied ? (
                <div className="applied-notice"><span>✅</span><span>لقد تقدمت لهذه الوظيفة</span></div>
              ) : user?.id === job.user_id ? (
                <div className="owner-notice"><span>هذه وظيفتك</span></div>
              ) : (
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => isAuthenticated ? navigate(`/jobs/${id}/apply`) : navigate('/auth/login')}>
                  📝 تقدم للوظيفة
                </button>
              )}
              <div className="divider" />
              <div className="apply-meta">
                <div className="apply-meta-item"><span>تاريخ النشر</span><span>{new Date(job.created_at).toLocaleDateString('ar')}</span></div>
                <div className="apply-meta-item"><span>نوع العمل</span><span>{JOB_TYPE_LABELS[job.job_type]}</span></div>
                {job.job_role && <div className="apply-meta-item"><span>الدور الوظيفي</span><span>{job.job_role}</span></div>}
              </div>
            </div>

            {job.org_name && (
              <div className="org-card card">
                <h4>عن المؤسسة</h4>
                <div className="org-brief">
                  <div className="org-logo-sm">{job.org_logo ? <img src={job.org_logo} alt="" /> : <span>{job.org_name?.[0]}</span>}</div>
                  <div>
                    <div className="fw-700">{job.org_name}</div>
                    <div className="text-muted text-sm">{job.org_type}</div>
                  </div>
                </div>
                {job.org_description && <p className="text-muted text-sm" style={{ marginTop: 12, lineHeight: 1.7 }}>{job.org_description.substring(0, 150)}...</p>}
                {job.website && <a href={job.website} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 12, width: '100%' }}>🌐 الموقع الرسمي</a>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>التقدم للوظيفة</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleApply}>
              <div className="form-group">
                <label className="form-label">رسالة التغطية</label>
                <textarea className="form-textarea" style={{ minHeight: 160 }} placeholder="اكتب رسالة تعرّف فيها بنفسك وبخبراتك وسبب اهتمامك بهذه الوظيفة..." value={application.cover_letter} onChange={e => setApplication(p => ({ ...p, cover_letter: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">رابط السيرة الذاتية (اختياري)</label>
                <input type="url" className="form-input" placeholder="https://..." value={application.resume_url} onChange={e => setApplication(p => ({ ...p, resume_url: e.target.value }))} />
              </div>
              <div className="flex gap-12">
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={applying}>{applying ? 'جاري الإرسال...' : 'إرسال الطلب'}</button>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
